import React, { useState } from 'react';
import { importHolidaysFromExcel } from '../../services/holidays.service.ts';
import { notification } from '../../utils/Notifications.tsx';
import Button from '../Button.tsx';

interface ImportResult {
  message: string;
  summary: {
    totalRows: number;
    holidays: {
      created: number;
      errors: number;
      errorsList: string[];
    };
    periods: {
      created: number;
      errors: number;
      errorsList: string[];
    };
    balances: {
      created: number;
      errors: number;
      errorsList: string[];
    };
  };
}

const ImportHolidaysExcel = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validar tipo de archivo
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/octet-stream'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        notification('Error', 'Solo se permiten archivos Excel (.xlsx, .xls)', 'danger');
        return;
      }
      
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      notification('Error', 'Por favor selecciona un archivo', 'danger');
      return;
    }

    setLoading(true);
    try {
      const result = await importHolidaysFromExcel(file);
      setResult(result);
      
      const { summary } = result;
      const totalCreated = summary.holidays.created + summary.periods.created + summary.balances.created;
      const totalErrors = summary.holidays.errors + summary.periods.errors + summary.balances.errors;
      
      if (totalErrors === 0) {
        notification('Éxito', `Importación completada. ${totalCreated} registros creados.`, 'success');
      } else {
        notification('Advertencia', `Importación completada con ${totalErrors} errores. ${totalCreated} registros creados.`, 'warning');
      }
    } catch (error) {
      console.error('Error importing file:', error);
      notification('Error', 'Error al importar el archivo', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card border-0 m-4">
      <div className="card-header bg-light">
        <h5 className="mb-0">
          <i className="fa fa-upload me-2"></i>
          Importar Vacaciones desde Excel
        </h5>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Seleccionar archivo Excel</label>
              <input
                type="file"
                className="form-control"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              <small className="text-muted">
                Formatos soportados: .xlsx, .xls
              </small>
            </div>
            
            <div className="d-flex gap-2 mb-3">
              <Button
                onClick={handleImport}
                disabled={!file || loading}
                variant="azul"
                title={loading ? 'Importando...' : 'Importar'}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Importando...
                  </>
                ) : (
                  <>
                    <i className="fa fa-upload me-2"></i>
                    Importar
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="alert alert-info">
              <h6><i className="fa fa-info-circle me-2"></i>Instrucciones:</h6>
              <ul className="mb-0">
                <li>El archivo debe contener encabezados en la primera fila</li>
                <li>Columnas requeridas: ID, NOMBRE COMPLETO o CURP (al menos uno)</li>
                <li>Para vacaciones: FECHA INICIO, FECHA FIN, DÍAS SOLICITADOS</li>
                <li>Para periodos: AÑO INICIO, AÑO FIN</li>
                <li>Para balances: DÍAS TOTALES, DÍAS DISFRUTADOS, DÍAS PENDIENTES</li>
              </ul>
            </div>
          </div>
        </div>

        {result && (
          <div className="mt-4">
            <h6>Resultado de la importación:</h6>
            <div className="row">
              <div className="col-md-4">
                <div className="card bg-success text-white">
                  <div className="card-body text-center">
                    <h4>{result.summary.holidays.created + result.summary.periods.created + result.summary.balances.created}</h4>
                    <small>Registros creados</small>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-warning text-dark">
                  <div className="card-body text-center">
                    <h4>{result.summary.holidays.errors + result.summary.periods.errors + result.summary.balances.errors}</h4>
                    <small>Errores</small>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-info text-white">
                  <div className="card-body text-center">
                    <h4>{result.summary.totalRows}</h4>
                    <small>Filas procesadas</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Detalles por tipo */}
            <div className="row mt-3">
              <div className="col-md-4">
                <h6>Vacaciones:</h6>
                <ul className="list-unstyled">
                  <li>Creadas: {result.summary.holidays.created}</li>
                  <li>Errores: {result.summary.holidays.errors}</li>
                </ul>
              </div>
              <div className="col-md-4">
                <h6>Periodos:</h6>
                <ul className="list-unstyled">
                  <li>Creados: {result.summary.periods.created}</li>
                  <li>Errores: {result.summary.periods.errors}</li>
                </ul>
              </div>
              <div className="col-md-4">
                <h6>Balances:</h6>
                <ul className="list-unstyled">
                  <li>Creados: {result.summary.balances.created}</li>
                  <li>Errores: {result.summary.balances.errors}</li>
                </ul>
              </div>
            </div>

            {/* Lista de errores */}
            {result.summary.holidays.errorsList.length > 0 && (
              <div className="mt-3">
                <h6>Errores de Vacaciones:</h6>
                <div className="alert alert-danger">
                  <ul className="mb-0">
                    {result.summary.holidays.errorsList.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {result.summary.periods.errorsList.length > 0 && (
              <div className="mt-3">
                <h6>Errores de Periodos:</h6>
                <div className="alert alert-danger">
                  <ul className="mb-0">
                    {result.summary.periods.errorsList.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {result.summary.balances.errorsList.length > 0 && (
              <div className="mt-3">
                <h6>Errores de Balances:</h6>
                <div className="alert alert-danger">
                  <ul className="mb-0">
                    {result.summary.balances.errorsList.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportHolidaysExcel;
